import isString from 'lodash/isString';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import type { UFOExperience } from '@atlassian/ufo';
import type {
	CloseExperienceFunction,
	ExperienceErrorReason,
	ExperienceMetadata,
	JPDExperience,
	MarkedJPDExperience,
	Marks,
} from './types.tsx';

export const createMarkedJpdExperience = <TMarks extends Marks>(
	ufoExperience: UFOExperience,
	marks: TMarks,
): MarkedJPDExperience<TMarks> => ({
	...createJpdExperience(ufoExperience),
	marks,
});

export const handleReason = (handler: CloseExperienceFunction, error: ExperienceErrorReason) => {
	if (error === undefined) {
		handler();
		return;
	}

	if (isString(error)) {
		handler({
			metadata: {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				error: error as string,
			},
		});
		return;
	}

	if (typeof error === 'object' && error !== null) {
		const errorStatus = 'statusCode' in error && error.statusCode ? `[${error.statusCode}] ` : '';
		const errorName = 'name' in error && error.name ? `${error.name}` : '';
		const errorMessage = 'message' in error ? ` - ${error.message}` : '';

		const errorString = `${errorStatus}${errorName}${errorMessage}`; // example: "[404] FetchError  - Not Found"

		const traceId = typeof error === 'object' && 'traceId' in error ? error.traceId : undefined;

		if (!errorName && !errorStatus && !errorMessage) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return handler(error as ExperienceMetadata);
		}

		return handler({
			metadata: {
				error: errorString,
				errors:
					'errors' in error && Array.isArray(error.errors)
						? error.errors
								.map((err) => {
									if (typeof err === 'string') {
										return err;
									}

									if (typeof err === 'object' && err !== null && 'error' in err) {
										return err.error;
									}

									return '';
								})
								.join(', ')
						: undefined,

				traceId,
			},
		});
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	handler(error as ExperienceMetadata);
};

export const createJpdExperience = (ufoExperience: UFOExperience): JPDExperience => ({
	ufoExperience,
	start: (startTime) => {
		ufoExperience.start();
		// hack to set custom start time
		if (startTime !== undefined) {
			Object.assign(ufoExperience.metrics, {
				...ufoExperience.metrics,
				startTime,
			});
		}
	},
	mark: (mark: string) => {
		ufoExperience.mark(mark);
	},
	success: (meta, endTime) => {
		ufoExperience.success(meta);
		// hack to set custom end time
		if (endTime !== undefined) {
			Object.assign(ufoExperience.metrics, {
				...ufoExperience.metrics,
				endTime,
			});
		}
	},
	successWithReason: (reason: ExperienceErrorReason) => {
		handleReason((meta) => ufoExperience.success(meta), reason);
	},
	failure: (reason: ExperienceErrorReason) => {
		handleReason((meta) => ufoExperience.failure(meta), reason);
	},
	abort: (reason) => {
		handleReason((meta) => ufoExperience.abort(meta), reason);
	},
});

export const wrapPromiseWithExperience = <R,>(
	promise: Promise<R>,
	experience: JPDExperience,
	errorAnalyticsId?: string,
): Promise<R> => {
	experience.start();
	return promise
		.then((response) => {
			experience.success();
			return response;
		})
		.catch((error) => {
			if (errorAnalyticsId) {
				fireErrorAnalytics({
					meta: {
						id: errorAnalyticsId,
						teamName: 'polaris',
					},
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: error as Error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}

			if (isClientFetchError(error)) {
				experience.abort(error);
				throw error;
			}

			if (isValidationError(error)) {
				experience.abort(error);
				throw error;
			}

			experience.failure(error);
			throw error;
		});
};

export const wrapWithExperience = <R,>(
	experience: JPDExperience,
	callback: () => Promise<R>,
): Promise<R> => {
	experience.start();
	return callback()
		.then((result) => {
			experience.success();
			return result;
		})
		.catch((error) => {
			if (isClientFetchError(error)) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				experience.abort(error as Error);
				throw error;
			}

			if (error instanceof Error) {
				experience.failure(error);
			}
			throw error;
		});
};

export const createExperienceWithMetaType = (
	experience: JPDExperience,
	metaType: string,
	parseMetadataError: (error?: Error) => string,
) => ({
	ufoExperience: experience.ufoExperience,
	start: experience.start,
	mark: experience.mark,
	success: (meta?: ExperienceMetadata, endTime?: number) => {
		experience.success(
			{
				metadata: {
					type: metaType,
				},
				...meta,
			},
			endTime,
		);
	},
	successWithReason: (error?: Error) => {
		experience.successWithReason({
			metadata: {
				error: parseMetadataError(error),
				type: metaType,
			},
		});
	},
	failure: (error?: Error) => {
		if (error && isClientFetchError(error)) {
			experience.abort({
				metadata: {
					error: parseMetadataError(error),
					type: metaType,
				},
			});
		} else {
			experience.failure({
				metadata: {
					error: parseMetadataError(error),
					type: metaType,
				},
			});
		}
	},
	abort: (error?: Error) => {
		experience.abort({
			metadata: {
				error: parseMetadataError(error),
				type: metaType,
			},
		});
	},
});
